import React, { ReactElement, useCallback } from "react"
import { GetServerSideProps, InferGetServerSidePropsType } from "next"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import * as P from "fp-ts/Predicate"
import { t, Trans } from "@lingui/macro"
import { Occupancy } from "@ssc-booking/api-contracts"
import getServerSidePropsWithDefaultSanityContent, {
    SanityContentProps,
} from "../../_common/getServerSidePropsWithDefaultSanityContent"
import { useECommerceMachine } from "../../_common/machines/machines-context"
import { FareCard } from "../_common/read-models/fare-card"
import SectionLoader from "../../../components/SectionLoader/SectionLoader"
import { maybeContentByFareCode } from "../_common/read-models/fare"
import { maybePaymentTermsByFareCode } from "../_common/payment-terms"
import config from "../../../pages/_app-config"
import { useHandleNoAvailability } from "../_common/hooks/use-handle-no-availability"
import { ECommerceContext } from "../_common/machines/ecommerce-machine/e-commerce-machine-context"
import SidebarLayout from "../../../components/SidebarLayout/SidebarLayout"
import PageTitle from "../../../components/PageTitle/PageTitle"
import InnerSectionContainer from "../../../components/InnerSectionContainer/InnerSectionContainer"
import ChangeGuests from "../../../components/ChangeGuests/ChangeGuests"
import useModal from "../../../components/ModalProvider/useModal"
import { useHandleGotoWebQuoteThankYou } from "../_common/hooks/use-handle-goto-webquote-thankyou"
import { usePreventRefresh } from "../payment/use-disable-back"
import { isWebQuoteSubmitting } from "../web-quote/WebQuoteModal/WebQuoteModal"
import { useValidateWebQuoteCommand } from "../_common/machines/ecommerce-machine/data-loading/validate-web-quote-command-machine"
import styles from "./fare.module.scss"
import FarePricesSection from "./components/FarePricesSection"
import FareCardLoader from "./components/FareCardLoader"

const renderFareCard =
    (sanityContent: Props["sanityContent"], context: ECommerceContext) =>
    // eslint-disable-next-line react/display-name
    (fareCard: FareCard): ReactElement | null => {
        const fareContent = maybeContentByFareCode(sanityContent.faresContent, fareCard.fare)
        const paymentTerms = maybePaymentTermsByFareCode(context, fareCard.fare)
        if (O.isNone(fareContent)) return null

        return (
            <FareCardLoader
                key={fareCard.key}
                fareCard={fareCard}
                cruiseGroup={context.cruiseGroup}
                inclusionsContent={sanityContent.inclusionsContent}
                promoBadgesContent={sanityContent.cardPromoBadgesContent}
                fareContent={fareContent.value}
                paymentTerms={paymentTerms}
            />
        )
    }

export const isShowable = F.pipe(FareCard.hasStatus("good"), P.or(FareCard.hasStatus("unknown")))

type Props = InferGetServerSidePropsType<typeof getServerSideProps>
const Fare: React.FC<React.PropsWithChildren<Props>> = ({ sanityContent }) => {
    const [state, send] = useECommerceMachine()
    const [stateWQ] = useValidateWebQuoteCommand(state)

    useHandleNoAvailability("fare")
    usePreventRefresh(isWebQuoteSubmitting(stateWQ))

    const { closeModalAfterSubmit } = useModal()
    useHandleGotoWebQuoteThankYou(() => closeModalAfterSubmit("close"))

    const fareCards = state.context.fareCards
    const withFlights = F.pipe(fareCards, ROA.filter(F.pipe(isShowable, P.and(FareCard.hasFlights))))
    const withoutFlights = F.pipe(fareCards, ROA.filter(F.pipe(isShowable, P.and(P.not(FareCard.hasFlights)))))
    const showableFareCards = F.pipe(withoutFlights, ROA.concat(withFlights))

    const onGuestsChange = useCallback((value: Occupancy) => send({ type: "CHANGE_GUESTS", guests: value }), [send])

    return (
        <SidebarLayout sanityContent={sanityContent} activeStep="fare">
            <PageTitle titleHead={t`Guest selection`} dataContentName="guests-heading" showFlowTitle>
                <Trans>Choose the number of guests</Trans>
            </PageTitle>
            <InnerSectionContainer
                backgroundColor="white"
                dataContentName="guests-selection-section"
                wrapperClassName={styles.changeGuests}
            >
                <ChangeGuests value={state.context.guests} onChange={onGuestsChange} />
                {state.context.flowVersion.ui.vsMemberCheckbox()}
            </InnerSectionContainer>

            <PageTitle titleHead={t`Fare type`} dataContentName="guests-heading">
                <Trans>Choose the fare that works best for you.</Trans>
            </PageTitle>
            {fareCards.length === 0 && <SectionLoader />}

            {showableFareCards.length > 0 && (
                <>
                    <div className={styles.notReadyToBook}>
                        {state.context.flowVersion.ui.suiteCategoryNotReadyToBookBanner(state)}
                    </div>
                    <FarePricesSection>
                        {showableFareCards.map(renderFareCard(sanityContent, state.context))}
                    </FarePricesSection>
                </>
            )}
        </SidebarLayout>
    )
}

export const getServerSideProps: GetServerSideProps<SanityContentProps<"penaltiesModalContent">> = async (context) =>
    await getServerSidePropsWithDefaultSanityContent(config(), context, ["penaltiesModalContent"])

export default Fare
